import { AsyncPipe, NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import {
  clearPresidioContextBannerChrome,
  schedulePresidioContextBannerChromeSync,
  syncPresidioContextBannerChrome,
} from '../utils/presidio-context-banner-chrome';

import { PresidioImpersonationFacade } from '../services/presidio-impersonation.facade';

/**
 * DEMO MVP 8.2.2 — global banner while CSR is viewing as an end user.
 * Keeps sticky header spacer / scroll-padding in sync so page scroll stays smooth.
 */
@Component({
  selector: 'prs-impersonation-banner',
  templateUrl: './presidio-impersonation-banner.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, NgIf, TranslatePipe],
})
export class PresidioImpersonationBannerComponent implements OnInit, AfterViewInit, OnDestroy {
  private facade = inject(PresidioImpersonationFacade);
  private cdr = inject(ChangeDetectorRef);
  private store = inject(Store);
  private host = inject(ElementRef<HTMLElement>);
  private readonly destroy$ = new Subject<void>();
  private resizeObserver?: ResizeObserver;

  isImpersonating$: Observable<boolean>;
  displayName$: Observable<string>;
  busy = false;

  ngOnInit(): void {
    this.facade.refresh();
    this.isImpersonating$ = this.facade.isImpersonating$;
    this.displayName$ = this.facade.impersonatedDisplayName$;

    this.isImpersonating$.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(() => {
      // Host is often display:contents — wait for layout, then remeasure sticky clearance.
      schedulePresidioContextBannerChromeSync(this.store);
    });
  }

  ngAfterViewInit(): void {
    if (!SSR && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.syncScrollChrome());
      // Prefer the shared context row — banner host may be display:contents (no box).
      const contextRow = document.querySelector('.prs-account-context-row');
      this.resizeObserver.observe(contextRow || this.host.nativeElement);
    }
    schedulePresidioContextBannerChromeSync(this.store);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.resizeObserver?.disconnect();
    this.clearScrollChrome();
  }

  async exit(): Promise<void> {
    if (this.busy) {
      return;
    }
    this.busy = true;
    this.cdr.markForCheck();
    try {
      await this.facade.end();
    } finally {
      this.busy = false;
      this.cdr.markForCheck();
      schedulePresidioContextBannerChromeSync(this.store);
    }
  }

  private syncScrollChrome(): void {
    syncPresidioContextBannerChrome(this.store);
  }

  private clearScrollChrome(): void {
    clearPresidioContextBannerChrome(this.store);
  }
}
